import React, { useState, useEffect } from 'react';
import { LineChart, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Stats {
  conversations: {
    total: number;
    byPlatform: {
      Instagram: number;
      WhatsApp: number;
    };
  };
  messages: {
    total: number;
    byPlatform: {
      Instagram: number;
      WhatsApp: number;
    };
  };
  timeSaved: number;
  sales: number;
  categories: Array<{
    name: string;
    count: number;
    percentage: number;
  }>;
  products: Array<{
    name: string;
    count: number;
  }>;
}

export const InteractionCharts = () => {
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    fetchStats();
  }, [startDate, endDate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      // Fetch conversations
      const { data: conversations } = await supabase
        .schema(profile.company_id)
        .from('conversaciones')
        .select('canal, estado, created_at')
        .gte('created_at', startDate)
        .lte('created_at', endDate);

      // Fetch messages
      const { data: messages } = await supabase
        .schema(profile.company_id)
        .from('crm_interactions')
        .select('plataforma, created_at, mensaje_es_de_cliente')
        .gte('created_at', startDate)
        .lte('created_at', endDate);

      // Fetch product categories
      const { data: categories } = await supabase
        .schema(profile.company_id)
        .from('requested_products')
        .select('categoria')
        .gte('created_at', startDate)
        .lte('created_at', endDate);

      // Fetch products
      const { data: products } = await supabase
        .schema(profile.company_id)
        .from('requested_products')
        .select('product')
        .gte('created_at', startDate)
        .lte('created_at', endDate);

      // Process conversations data
      const conversationsTotal = conversations?.length || 0;
      const conversationsByPlatform = {
        Instagram: conversations?.filter(c => c.canal === 'Instagram').length || 0,
        WhatsApp: conversations?.filter(c => c.canal === 'WhatsApp').length || 0,
      };

      // Process messages data
      const messagesTotal = messages?.filter(m => !m.mensaje_es_de_cliente).length || 0;
      const messagesByPlatform = {
        Instagram: messages?.filter(m => m.plataforma === 'Instagram' && !m.mensaje_es_de_cliente).length || 0,
        WhatsApp: messages?.filter(m => m.plataforma === 'WhatsApp' && !m.mensaje_es_de_cliente).length || 0,
      };

      // Process categories data
      const categoryCounts = categories?.reduce((acc: { [key: string]: number }, item) => {
        if (item.categoria) {
          acc[item.categoria] = (acc[item.categoria] || 0) + 1;
        }
        return acc;
      }, {}) || {};

      const totalCategories = Object.values(categoryCounts).reduce((a, b) => a + b, 0);
      const processedCategories = Object.entries(categoryCounts).map(([name, count]) => ({
        name,
        count,
        percentage: (count / totalCategories) * 100
      })).sort((a, b) => b.count - a.count);

      // Process products data
      const productCounts = products?.reduce((acc: { [key: string]: number }, item) => {
        if (item.product) {
          acc[item.product] = (acc[item.product] || 0) + 1;
        }
        return acc;
      }, {}) || {};

      const processedProducts = Object.entries(productCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      // Calculate sales
      const sales = conversations?.filter(c => c.estado === 'sold').length || 0;

      setStats({
        conversations: {
          total: conversationsTotal,
          byPlatform: conversationsByPlatform,
        },
        messages: {
          total: messagesTotal,
          byPlatform: messagesByPlatform,
        },
        timeSaved: messagesTotal * 5, // 5 minutes per message
        sales,
        categories: processedCategories,
        products: processedProducts,
      });

    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err instanceof Error ? err.message : 'Error loading statistics');
    } finally {
      setLoading(false);
    }
  };

  const PlatformPieChart: React.FC<{
    instagram: number;
    whatsapp: number;
    total: number;
  }> = ({ instagram, whatsapp, total }) => {
    const instagramPercentage = (instagram / total) * 100;
    const whatsappPercentage = (whatsapp / total) * 100;

    return (
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20">
          <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
            {/* Instagram slice */}
            <path
              d={`M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831`}
              fill="none"
              stroke="#E4E4E7"
              strokeWidth="3"
            />
            <path
              d={`M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831`}
              fill="none"
              stroke="#E11D48"
              strokeWidth="3"
              strokeDasharray={`${instagramPercentage}, 100`}
            />
            {/* WhatsApp slice */}
            <path
              d={`M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831`}
              fill="none"
              stroke="#22C55E"
              strokeWidth="3"
              strokeDasharray={`${whatsappPercentage}, 100`}
              strokeDashoffset={`-${instagramPercentage}`}
            />
          </svg>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-rose-600" />
            <span className="text-sm">
              Instagram ({instagram}) 
              <span className="text-gray-500 ml-1">
                {instagramPercentage.toFixed(1)}%
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-sm">
              WhatsApp ({whatsapp})
              <span className="text-gray-500 ml-1">
                {whatsappPercentage.toFixed(1)}%
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <LineChart className="w-8 h-8 text-gray-700" />
          <h1 className="text-2xl font-semibold text-gray-900">Gráficos de Interacción</h1>
        </div>

        {/* Date Range Selector */}
        <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow-sm">
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
              Desde
            </label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
              Hasta
            </label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Conversations Card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Conversaciones creadas
          </h3>
          <div className="mb-6">
            <div className="text-4xl font-bold text-gray-900 mb-4">
              {stats?.conversations.total}
            </div>
            <PlatformPieChart
              instagram={stats?.conversations.byPlatform.Instagram || 0}
              whatsapp={stats?.conversations.byPlatform.WhatsApp || 0}
              total={stats?.conversations.total || 0}
            />
          </div>
        </div>

        {/* Messages Card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Mensajes respondidos por IA
          </h3>
          <div className="mb-6">
            <div className="text-4xl font-bold text-gray-900 mb-4">
              {stats?.messages.total}
            </div>
            <PlatformPieChart
              instagram={stats?.messages.byPlatform.Instagram || 0}
              whatsapp={stats?.messages.byPlatform.WhatsApp || 0}
              total={stats?.messages.total || 0}
            />
          </div>
        </div>

        {/* Time Saved Card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Tiempo ahorrado con respuestas automáticas
          </h3>
          <div className="text-4xl font-bold text-gray-900 mb-2">
            {stats?.timeSaved || 0}
          </div>
          <div className="text-sm text-gray-500">minutos</div>
        </div>

        {/* Sales Card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Ventas cerradas
          </h3>
          <div className="text-4xl font-bold text-gray-900">
            {stats?.sales || 0}
          </div>
        </div>

        {/* Categories Card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Categorías preguntadas
          </h3>
          <div className="space-y-4">
            {stats?.categories.map((category) => (
              <div key={category.name}>
                <div className="flex justify-between items-center mb-1">
                  <span className="font-medium">{category.name}</span>
                  <span className="text-sm text-gray-500">
                    {category.count} ({category.percentage.toFixed(1)}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${category.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Products Card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Productos preguntados
          </h3>
          <div className="space-y-2">
            {stats?.products.map((product) => (
              <div key={product.name} className="flex justify-between items-center">
                <span className="truncate flex-1">{product.name}</span>
                <span className="text-sm text-gray-500 ml-2">{product.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};